import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { actions, selectors } from '@elab/core/store/store-locator';
import { actions as globalActions } from '@elab/core/store/global';
import { link } from '@elab/core/utils/htmlTemplates';

/**
 * Map popup component, created for e2-map (to render map point pop up data).
 *
 * @attr {Number} index - index of post point from store-locator
 * @attr {String} [action-type] - redux action type for action button
 * @attr {String} [action-button-text] - Label to be shown inside button
 * @attr {Boolean} [show-opening-hours] - show table with opening hours information
 * @attr {Boolean} [name-as-link] - use store name as link
 * @attr {Boolean} [reset-default-style] - reset default popup styles (make custom popup)
 * @attr {String} [more-info] - show link to Single Store page
 * @attr {Array} [render-as-button] - list of elements names which should be rendered as a button
 * @attr {Boolean} [phone-with-link] - make phone field callable
 *
 * @prop {Object} store - store data from store-locator (based on index)

 * Example:
 * <e2-store-info-window
 *  index="0"
 *  action-type="STORE-LOCATOR.TO_DO"
 *  show-opening-hours
 *  name-as-link
 *  reset-default-style
 *  more-info
 *  render-as-button="["phone", "email"]"
 * ></e2-store-info-window>
 */

class StoreInfoWindow extends SimpleLitElement {
    static get properties() {
        return {
            point: { type: Object, attribute: false },
            index: { type: Number },
            actionType: { type: String, attribute: 'action-type' },
            actionButtonText: { type: String, attribute: 'action-button-text' },
            showOpeningHours: {
                type: Boolean,
                attribute: 'show-opening-hours',
            },
            formattedCityWithCountry: {
                type: Boolean,
                attribute: 'formatted-city-with-country',
            },
            nameAsLink: { type: Boolean, attribute: 'name-as-link' },
            resetDefaultStyle: {
                type: Boolean,
                attribute: 'reset-default-style',
            },
            moreInfo: { type: String, attribute: 'more-info' },
            closedLabel: {
                type: String,
                attribute: 'closed-label',
            },
            renderAsButton: {
                type: Array,
                attribute: 'render-as-button',
            },
            phoneWithLink: {
                type: Boolean,
                attribute: 'phone-with-link',
            },
            noCountdown: {
                type: Boolean,
                attribute: 'no-countdown',
            },
            noContactDetails: {
                type: Boolean,
                attribute: 'no-contact-details',
            },
            infoWindowCountdownTitleOpened: {
                type: String,
                attribute: 'info-window-countdown-title-opened',
            },
            infoWindowCountdownTitleClosed: {
                type: String,
                attribute: 'info-window-countdown-title-closed',
            },
            infoWindowHoursTitle: {
                type: String,
                attribute: 'info-window-hours-title',
            },
            selectedStore: { type: Object, attribute: false },
            isOutside: {
                type: Boolean,
                attribute: 'is-outside',
            },
            isClosed: {
                type: Boolean,
                attribute: 'closed',
                reflect: true,
            },
            closeButtonLabel: {
                type: String,
                attribute: 'close-button-label',
            },
        };
    }

    constructor() {
        super();

        this.point = {};
        this.index = 0;
        this.moreInfo = '';
        this.renderAsButton = [];
        this.actionButtonText = '';
        this.noCountdown = false;
        this.noContactDetails = false;
        this.infoWindowHoursTitle = '';
        this.isOutside = false;
        this.isClosed = false;
        this.closeButtonLabel = 'Close';
    }

    updated(changedProperties) {
        if (changedProperties.has('selectedStore')) {
            this.windowToggle(this.selectedStore && this.selectedStore.storeId);
        }
    }

    windowToggle(open = false) {
        if (this.isOutside) {
            this.isClosed = !open;
            if (this.selectedStore && this.selectedStore.storeId && this.isClosed) {
                this.dispatchEvent(new CustomEvent('select-store', { detail: false }));
            }
        }
    }

    getActionButtonTemplate = () =>
        this.actionType
            ? html`
                  <e2-action-button
                      no-arrow
                      action-type="${this.actionType}"
                      size="medium"
                      align="left"
                      class-modifier="button--no-hover-effect button--brand-link button--info-window button--link-small"
                      class="info-window__link"
                      button-label="${this.actionButtonText}"
                  >
                  </e2-action-button>
              `
            : null;

    getOpenHoursTemplate = weekDayOpeningList =>
        this.showOpeningHours
            ? html`
                  <div class="info-window__hours">
                      <div class="info-window__title">
                          ${this.infoWindowHoursTitle}
                      </div>
                      <e2-hours-table
                          class="info-window__table"
                          .openHours="${weekDayOpeningList}"
                          hours-separator="-"
                          css-class="table--info-window"
                          closed-label="${this.closedLabel}"
                      >
                      </e2-hours-table>
                  </div>
              `
            : null;

    getCountdownTemplate = name => html`
        <e2-store-countdown
            class="info-window__countdown"
            store-code="${name}"
            format="SECONDS"
            title-open="${this.infoWindowCountdownTitleOpened}"
            title-closed="${this.infoWindowCountdownTitleClosed}"
        >
        </e2-store-countdown>
    `;

    getPhoneTemplate = phone => {
        const cssClasses = `${
            this.renderAsButton.includes('phone') ? 'button' : ''
        } info-window__phone`;

        return phone
            ? html`
                  ${this.phoneWithLink
                      ? html`
                            <a href="tel:${phone}" class="${cssClasses}">
                                ${phone}
                            </a>
                        `
                      : html`
                            <p class="${cssClasses}">${phone}</p>
                        `}
              `
            : null;
    };

    getFeaturesTemplate = entry => {
        return entry.length
            ? html`
                  <div class="info-window__features">
                      ${entry.map(
                          feature =>
                              html`
                                  <div class="info-window__feature">${feature.value}</div>
                              `,
                      )}
                  </div>
              `
            : '';
    };

    render() {
        const {
            displayName,
            address = {},
            openingHours = {},
            url = '',
            normalizedName = '',
            features = {},
        } = this.point;
        const {
            town = '',
            line1 = '',
            line2 = '',
            postalCode = '',
            phone = '',
            email = '',
            country = {},
        } = address;
        const { weekDayOpeningList = [] } = openingHours;
        const { name = '' } = country;
        const storeName = displayName || '';

        const storeNameContent = this.nameAsLink
            ? link({ className: 'info-window__link', href: url }, storeName)
            : storeName;

        return html`
            <div class="info-window">
                ${this.isOutside
                    ? html`
                          <div
                              class="info-window__close"
                              title="${this.closeButtonLabel}"
                              @click="${() => this.windowToggle()}"
                          ></div>
                      `
                    : ''}
                <div class="info-window__container">
                    <div class="info-window__address">
                        <p class="info-window__name">
                            ${storeNameContent}
                        </p>
                        <p class="info-window__lines">
                            <span class="info-window__line1">
                                ${this.formattedCityWithCountry ? `${line1} ${line2}` : line1}
                            </span>
                            ${this.formattedCityWithCountry
                                ? ''
                                : html`
                                      <span class="info-window__line2">${line2}</span>
                                  `}
                        </p>
                        <p class="info-window__postal-code">
                            ${this.formattedCityWithCountry ? `${postalCode} ${town}` : postalCode}
                        </p>
                        <p class="info-window__town">
                            ${this.formattedCityWithCountry ? name : town}
                        </p>
                        ${this.getFeaturesTemplate(features.entry || [])}
                    </div>
                    ${!this.noContactDetails
                        ? html`
                              <div class="info-window__contacts">
                                  ${this.getPhoneTemplate(phone)}
                                  ${email
                                      ? html`
                                            <a
                                                class="${this.renderAsButton.includes('email')
                                                    ? 'button'
                                                    : ''} info-window__link info-window__link--email"
                                                href="mailto:${email}"
                                                >${email}</a
                                            >
                                        `
                                      : null}
                                  ${this.moreInfo
                                      ? link(
                                            {
                                                className: `${
                                                    this.renderAsButton.includes('more-info')
                                                        ? 'button'
                                                        : ''
                                                } info-window__link info-window__link--more-details`,
                                                href: url,
                                            },
                                            this.moreInfo,
                                        )
                                      : null}
                              </div>
                          `
                        : null}
                    ${this.getActionButtonTemplate()}
                    ${this.getOpenHoursTemplate(weekDayOpeningList)}
                    ${!this.noCountdown ? this.getCountdownTemplate(normalizedName) : null}
                </div>
                ${this.resetDefaultStyle
                    ? html`
                          <div class="info-window__arrow"></div>
                      `
                    : null}
            </div>
        `;
    }
}

const mapStateToProps = (state, ctx) => {
    const selectedStore = selectors.getSelectedStore(state);
    const { storeId = '' } = selectedStore || {};

    const storePoint =
        ctx.isOutside && storeId
            ? selectors.getStores(state).find(store => store.name == storeId)
            : selectors.getStoreByIndex(state, ctx.index);

    return {
        point: storePoint,
        noCountdown: ctx.noCountdown,
        noContactDetails: ctx.noContactDetails,
        infoWindowHoursTitle: ctx.infoWindowHoursTitle,
        selectedStore,
    };
};

const mapDispatchToEvents = dispatch => ({
    redirect: e => dispatch(globalActions.redirect(e.detail)),
    'select-store': e => dispatch(actions.selectStore({ storeId: e.detail })),
});

export default () =>
    customElements.define(
        'e2-store-info-window',
        connect(mapStateToProps, mapDispatchToEvents)(StoreInfoWindow),
    );
